/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, ReactNode, Ref, useState } from "react";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { ThemeProvider } from "@material-ui/styles";
import {
  KeyboardDatePicker,
  KeyboardDatePickerProps,
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { isValid } from "date-fns";
import enLocale from "date-fns/locale/en-US";
import csLocale from "date-fns/locale/cs";
import { LangCode } from "src/requests/localization/types";
import { DateType } from "../../../../globalTypes";
import { DateConfig } from "../../../organisms/TableData/types";
import { dateFieldCss } from "../commonStyles";
import InputComponent from "../InputComponent";
import { noop } from "../../../../globalConsts";
import useMuiTheme from "./useMuiTheme";
import useDateConfiguration from "./useDateConfiguration";
import { useLocalizationString } from "../../../../store/localization/useSelectors";

export type DateViewFormat = "day only" | "full" | "time only";

type UncontrolledOnlyProps = { value?: undefined; defaultValue: DateType };
type ControlledOnlyProps = { value: DateType; defaultValue?: undefined };

type Props = (UncontrolledOnlyProps | ControlledOnlyProps) & {
  dateFormat?: DateViewFormat;
  onChange?: (newDate: string | null) => void;
  onBlur?(): void;
  disabled?: boolean;
  defaultValue?: string | Date;
  inputRef?: Ref<HTMLInputElement>;
  name?: string;
  id?: string;
  config?: DateConfig;
  required?: boolean;
  dataCy?: string;
  className?: string;
};

type DatePickerProps = Pick<
  KeyboardDatePickerProps,
  | "value"
  | "onChange"
  | "placeholder"
  | "format"
  | "clearable"
  | "minDate"
  | "maxDate"
  | "inputRef"
  | "name"
  | "id"
  | "defaultValue"
  | "InputProps"
  | "css"
  | "disabled"
  | "helperText"
  | "clearLabel"
  | "cancelLabel"
  | "required"
  | "onBlur"
  | "className"
>;

const LOCALE_MAP = {
  [LangCode.Czech]: csLocale,
  [LangCode.English]: enLocale,
};

const FlowioDateInput: FC<Props> = ({
  onChange = noop,
  onBlur,
  inputRef = null,
  defaultValue,
  value,
  disabled,
  name,
  config,
  dateFormat = "full",
  id,
  dataCy = "flowio-date-input",
  required = false,
  className,
}) => {
  const [isDateValid, setIsDateValid] = useState(true);
  const muiTheme = useMuiTheme();
  const localizationString = useLocalizationString();

  const {
    dateFormat: format,
    ampm,
    placeholder,
    constraints,
    isDayOnlyFormat,
    helperText,
    cancelLabel,
    clearLabel,
  } = useDateConfiguration(dateFormat, value, config);

  const handleNewChange = (date: MaterialUiPickersDate): void => {
    if (date === null) {
      setIsDateValid(true);
      onChange(null);
      return;
    }
    if (!isValid(date)) {
      setIsDateValid(false);
      return;
    }

    const formattedDate = date.toISOString();

    onChange(formattedDate);
    setIsDateValid(true);
  };

  const hasError = !isDateValid || (required && !value);
  const sameProps: DatePickerProps = {
    value: value ? new Date(value) : null,
    onChange: handleNewChange,
    placeholder,
    disabled,
    defaultValue,
    name,
    id,
    inputRef,
    minDate: constraints.min,
    maxDate: constraints.max,
    format,
    clearable: true,
    InputProps: {
      disableUnderline: true,
      inputComponent: InputComponent,
    },
    css: dateFieldCss(hasError),
    required,
    helperText,
    clearLabel,
    cancelLabel,
    onBlur,
    className,
  };

  const getRightPicker = (): ReactNode => {
    if (isDayOnlyFormat) {
      return <KeyboardDatePicker data-cy={dataCy} {...sameProps} />;
    }
    return (
      <KeyboardDateTimePicker data-cy={dataCy} {...sameProps} ampm={ampm} />
    );
  };

  return (
    <MuiPickersUtilsProvider
      utils={DateFnsUtils}
      locale={LOCALE_MAP[localizationString]}
    >
      <ThemeProvider theme={muiTheme}>{getRightPicker()}</ThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

export default FlowioDateInput;
export type FlowioDateInputProps = Props;
